import { getSafeInternalRedirect } from "@/lib/auth-redirect";
import { setClientCookie } from "@/lib/cookies";
import { getRootDomain } from "@/lib/urls";

type OauthReturnContext = "portal" | "saas";

type OauthReturnOptions = {
  context?: OauthReturnContext;
  maxAge?: number;
  origin?: string;
  returnTo?: string | null;
};

const DEFAULT_MAX_AGE_SECONDS = 10 * 60;

function getCookieDomains(): Array<string | undefined> {
  const domains = new Set<string | undefined>([undefined]);

  if (typeof window === "undefined") {
    return Array.from(domains);
  }

  const rootDomain = getRootDomain().replace(/^www\./i, "");
  const hostname = window.location.hostname.replace(/^www\./i, "");
  const isLocalhost =
    rootDomain === "localhost" ||
    hostname === "localhost" ||
    /^127\./.test(hostname);

  if (
    rootDomain &&
    !isLocalhost &&
    (hostname === rootDomain || hostname.endsWith(`.${rootDomain}`))
  ) {
    domains.add(`.${rootDomain}`);
  }

  return Array.from(domains);
}

export function setOauthReturnCookies({
  context,
  maxAge = DEFAULT_MAX_AGE_SECONDS,
  origin,
  returnTo,
}: OauthReturnOptions): string {
  const safeReturnTo = getSafeInternalRedirect(returnTo, "/dashboard");
  const domains = getCookieDomains();

  for (const domain of domains) {
    const cookieOptions = {
      domain,
      maxAge,
      sameSite: "lax" as const,
    };

    setClientCookie("oauth_return_to", safeReturnTo, cookieOptions);

    if (context) {
      setClientCookie("oauth_return", context, cookieOptions);
    }

    if (origin) {
      setClientCookie("oauth_return_origin", origin, cookieOptions);
    }
  }

  return safeReturnTo;
}

export function buildOauthReturnSearchParams(
  options: OauthReturnOptions,
): URLSearchParams {
  const safeReturnTo = setOauthReturnCookies(options);
  const params = new URLSearchParams({ returnTo: safeReturnTo });

  if (options.context) {
    params.set("returnContext", options.context);
  }

  if (options.origin) {
    params.set("returnOrigin", options.origin);
  }

  return params;
}
